import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import analytics from "web/script/analytics/analytics";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { gettext } from "web/script/modules/django-i18n";
import classnames from "classnames";
import withReduxProvider from "web/react/redux-provider";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { hideMainNavigation } from "web/redux/ducks/main-navigation";
import environment from "web/script/modules/environment";
import styles from "./main-navigation-desktop.css";

const CATEGORY_LIMIT = 12;

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
letters.push("0-9");

function extractVisibility(state) {
    return state.mainNavigationReducer.visible;
}

export function MainNavigationDesktop({ menuData, featuredDesignersUrl }) {
    const dispatch = useDispatch();
    const showContent = useReduxSelector(extractVisibility);
    const categories = menuData.menus;
    const links = menuData.links;
    const isRebrand = environment.getFeature("seo_global_rebrand_2020");
    const iconName = isRebrand ? "arrow-icon" : "chevron";
    const [selectedCategory, setSelectedCategory] = useState(menuData.menus[0]);
    const [selectedSubMenu, setSelectedSubMenu] = useState(menuData.menus[0].menus[0]);
    const [selectedTrendMenu, setSelectedTrendMenu] = useState(null);
    const [popularBrands, setPopularBrands] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({});

    function selectCategory(category) {
        setSelectedCategory(category);
        setSelectedSubMenu(category.menus[0]);
        setSelectedTrendMenu(null);
        setPopularBrands(null);
    }

    function selectPopularBrands(category) {
        setSelectedCategory(category);
        setSelectedSubMenu(menuData.menus[0].menus[0]);
        setSelectedTrendMenu(null);
        setPopularBrands(category.links);
    }

    useEffect(() => {
        setSelectedCategory(menuData.menus[0]);
        setSelectedSubMenu(menuData.menus[0].menus[0]);
        setSelectedTrendMenu(null);
        setPopularBrands(null);
    }, [menuData]);

    function getInteraction(callback, label) {
        return {
            ...{
                onClick: () => {
                    analytics.event("Menu click", "Clicked", label);
                    callback();
                },
            },
        };
    }

    function renderCategories() {
        return (
            <ul className={styles.menu}>
                {categories.map((category) => {
                    const isPopularBrands = category.name === "popular_brands";
                    function interact() {
                        if (isPopularBrands) {
                            selectPopularBrands(category);
                        } else {
                            selectCategory(category);
                        }
                    }
                    const interaction = getInteraction(interact, "Category");
                    let displayText;

                    if (isPopularBrands) {
                        displayText = gettext("app_header_menu.brands_title");
                    }

                    if (!displayText) {
                        displayText = category.display_name;
                    }
                    return (
                        <li
                            key={category.display_name}
                            className={classnames(styles.menuItem, {
                                [styles.activeMenuItem]: selectedCategory.name === category.name,
                                [styles.clickable]: true,
                            })}
                            {...interaction}
                        >
                            {displayText}
                        </li>
                    );
                })}
                {links &&
                    links.map((link) => {
                        const isSaleLink = link.is_sale;
                        return (
                            <li key={link.text} className={classnames(styles.menuItem)}>
                                {isSaleLink && (
                                    <SVGIcon className={classnames(styles.saleTag)} name="tag" />
                                )}
                                <a href={link.url} className={styles.saleLink}>
                                    {link.text}
                                </a>
                            </li>
                        );
                    })}
            </ul>
        );
    }

    function renderSubCategories() {
        const totalItems = selectedCategory.menus.length + selectedCategory.links.length;
        const hasHiddenItems = totalItems >= CATEGORY_LIMIT;
        const hideItems = hasHiddenItems && !expandedCategories[selectedCategory.name];

        return (
            <div
                className={classnames(styles.subMenu, {
                    [styles.hideExtraLinks]: hideItems,
                })}
            >
                <ul className={styles.subMenu}>
                    {selectedCategory.menus.map((menu) => {
                        function interact() {
                            setSelectedSubMenu(menu);
                            setSelectedTrendMenu(null);
                        }
                        const interaction = getInteraction(interact, "Subcategory");
                        return (
                            <li
                                key={menu.display_name}
                                className={classnames(styles.subMenuItem, {
                                    [styles.subMenuItemActive]: selectedSubMenu.name === menu.name,
                                    [styles.clickable]: true,
                                })}
                                {...interaction}
                            >
                                {menu.display_name}
                                <SVGIcon className={styles.subMenuChevron} name={iconName} />
                            </li>
                        );
                    })}

                    {selectedCategory.links &&
                        selectedCategory.links.map((link) => {
                            return (
                                <li
                                    key={link.text}
                                    className={classnames(styles.subMenuItem, {
                                        [styles.saleMenuItem]: link.is_sale,
                                    })}
                                >
                                    {link.is_sale && (
                                        <SVGIcon className={styles.saleTag} name="tag" />
                                    )}
                                    <a href={link.url} className={styles.subMenuLink}>
                                        {link.text}
                                    </a>
                                </li>
                            );
                        })}
                </ul>
                {hideItems && (
                    <button
                        type="button"
                        className={styles.moreButton}
                        onClick={() =>
                            setExpandedCategories({
                                ...expandedCategories,
                                [selectedCategory.name]: true,
                            })
                        }
                    >
                        {"+ "}
                        {gettext("burger_menu.more_category_link")} {/* More */}
                    </button>
                )}
                <ul className={styles.trendList}>
                    {selectedCategory.trend_menus &&
                        selectedCategory.trend_menus.map((trendMenu) => {
                            function interact() {
                                setSelectedSubMenu(trendMenu);
                                setSelectedTrendMenu(null);
                            }
                            const interaction = getInteraction(interact, "Trends");
                            return (
                                <li
                                    key={trendMenu.display_name}
                                    className={classnames(styles.subMenuItem, styles.trendItem, {
                                        [styles.subMenuItemActive]:
                                            selectedSubMenu.name === trendMenu.name,
                                        [styles.clickable]: true,
                                    })}
                                    {...interaction}
                                >
                                    {trendMenu.display_name}
                                    <SVGIcon className={styles.subMenuChevron} name={iconName} />
                                </li>
                            );
                        })}
                </ul>
            </div>
        );
    }

    function renderSubCategoryLinks() {
        return (
            <ul className={styles.subMenu}>
                {Array.isArray(selectedSubMenu.links) &&
                    selectedSubMenu.links.map((link) => {
                        if (link.url === "") return;
                        return (
                            <li
                                key={link.text}
                                className={classnames(styles.subMenuItem, {
                                    [styles.saleMenuItem]: link.is_sale,
                                })}
                            >
                                {link.is_sale && <SVGIcon className={styles.saleTag} name="tag" />}
                                <a href={link.url} className={styles.subMenuLink}>
                                    {link.text}
                                </a>
                            </li>
                        );
                    })}

                {Array.isArray(selectedSubMenu.trend_menus) &&
                    selectedSubMenu.trend_menus.map((trendMenu) => {
                        const interaction = getInteraction(
                            () => setSelectedTrendMenu(trendMenu),
                            "Trends"
                        );
                        return (
                            <li
                                key={trendMenu.display_name}
                                className={classnames(styles.subMenuItem, styles.trendItem, {
                                    [styles.subMenuItemActive]:
                                        selectedTrendMenu &&
                                        selectedTrendMenu.name === trendMenu.name,
                                    [styles.clickable]: true,
                                })}
                                {...interaction}
                            >
                                {trendMenu.display_name}
                                <SVGIcon className={styles.subMenuChevron} name={iconName} />
                            </li>
                        );
                    })}
            </ul>
        );
    }

    function renderTrendLinks() {
        if (!selectedTrendMenu) return;
        return (
            <ul className={styles.subMenu}>
                {selectedTrendMenu.links.map((link) => {
                    return (
                        <li
                            key={link.url}
                            className={classnames(styles.subMenuItem, {
                                [styles.saleMenuItem]: link.is_sale,
                            })}
                        >
                            <a href={link.url} className={styles.subMenuLink}>
                                {link.text}
                            </a>
                        </li>
                    );
                })}
            </ul>
        );
    }

    function renderPopularBrands() {
        return (
            <div className={styles.subMenusContainer}>
                <div className={styles.popularBrands}>
                    <span className={styles.subMenuTitle}>
                        {gettext("app_header_menu.popular_brands")}
                    </span>
                    <ul className={styles.popularBrandsList}>
                        {popularBrands.map((link) => (
                            <li
                                key={link.url}
                                className={classnames(styles.subMenuItem, styles.popularBrandItem)}
                            >
                                <a href={link.url} className={classnames(styles.subMenuLink)}>
                                    {link.text}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <span className={styles.subMenuTitle}>
                        {gettext("app_header_menu.brands_a_z")}
                    </span>
                    <div className={styles.brandLetters}>
                        {letters.map((letter) => (
                            <a
                                key={`brand-initial-${letter}`}
                                href={`${featuredDesignersUrl}#${letter}`}
                                className={styles.brandLetter}
                            >
                                {letter}
                            </a>
                        ))}
                    </div>
                    <a href={featuredDesignersUrl} className={styles.viewAllBrands}>
                        {gettext("app_header_menu.view_all")}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                className={classnames(styles.mainNavigationOverlay, {
                    [styles.show]: showContent,
                })}
                onClick={() => {
                    dispatch(hideMainNavigation());
                }}
            />
            <div
                className={classnames(styles.mainNavigationDesktop, {
                    [styles.show]: showContent,
                })}
            >
                {renderCategories()}
                <CloseButton
                    className={styles.closeButton}
                    onClick={() => {
                        dispatch(hideMainNavigation());
                    }}
                />
                {popularBrands ? (
                    renderPopularBrands()
                ) : (
                    <div className={styles.subMenusContainer}>
                        {renderSubCategories()}
                        {renderSubCategoryLinks()}
                        {renderTrendLinks()}
                    </div>
                )}
            </div>
        </>
    );
}

MainNavigationDesktop.propTypes = {
    menuData: PropTypes.shape({
        menus: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                menus: PropTypes.array,
                trend_menus: PropTypes.array,
            })
        ).isRequired,
        links: PropTypes.arrayOf(
            PropTypes.shape({
                text: PropTypes.string,
                is_sale: PropTypes.bool,
                url: PropTypes.string,
            })
        ),
    }),
    featuredDesignersUrl: PropTypes.string.isRequired,
};

export default withReduxProvider(MainNavigationDesktop);
